import { Icon } from '@eventbrite/eds-icon';
import { ArrowLeftChunky, ArrowRightChunky } from '@eventbrite/eds-iconography';
import { gettext } from '@eventbrite/i18n';
import React, { useRef, useState } from 'react';
import { LISTING_HEAP_IDS } from '../../../../constants';
import { FeaturedVideoProps } from '../../../../types';
import { FetchPriorityType, HeroImage, LoadingType } from '../HeroImage';
import { HeroVideo } from '../HeroVideo/HeroVideo';
import {
    AccessibleText,
    CarouselViewport,
    CarouselWrapper,
    ControlButton,
    ImageList,
    ImageListItem,
    SliderBar,
    SliderBarButton,
    SliderBarList,
    SliderBarOverlay,
} from './HeroCarousel.styles';
import type { CarouselItems } from './index';
import { useKeyPress } from './useKeyPress';
import { useSwipe } from './useSwipe';

interface HeroCarousel {
    eventName: string;
    items: Array<CarouselItems>;
    initialSlide?: number;
    featuredVideo?: FeaturedVideoProps | undefined;
}

const TRANSLATION_UNIT_INIT = '0';

const getSlidesTranslationValue = (slidePosition: number) => {
    return slidePosition === 0 ? TRANSLATION_UNIT_INIT : `-${slidePosition}00`;
};

export const HeroCarousel: React.FC<HeroCarousel> = ({
    eventName,
    items,
    initialSlide = 0,
    featuredVideo,
}) => {
    const shouldShowCarouselControls = items.length > 1;
    const firstSlide = 0;
    const lastSlide = items.length - 1;
    const [currentSlide, setCurrentSlide] = useState(initialSlide);
    const [translationUnit, setTranslationUnit] = useState(
        getSlidesTranslationValue(initialSlide),
    );
    const [isCarouselFocused, setIsCarouselFocused] = useState(false);
    const carousel = useRef(null);

    const nextSlide = () => {
        if (currentSlide === lastSlide) {
            setCurrentSlide(firstSlide);
            setTranslationUnit(getSlidesTranslationValue(firstSlide));
        } else {
            setCurrentSlide(currentSlide + 1);
            setTranslationUnit(getSlidesTranslationValue(currentSlide + 1));
        }
    };

    const prevSlide = () => {
        if (currentSlide === firstSlide) {
            setCurrentSlide(lastSlide);
            setTranslationUnit(getSlidesTranslationValue(lastSlide));
        } else {
            setCurrentSlide(currentSlide - 1);
            setTranslationUnit(getSlidesTranslationValue(currentSlide - 1));
        }
    };

    const handleOnFocus = () => {
        if (!isCarouselFocused) {
            setIsCarouselFocused(true);
        }
    };
    const handleOnBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        const isCarouselBlurred = !event.currentTarget.contains(
            event.relatedTarget as Node,
        );
        if (isCarouselBlurred) {
            setIsCarouselFocused(false);
        }
    };

    useKeyPress({
        isCarouselFocused,
        onLeftKeyPressed: () => prevSlide(),
        onRightKeyPressed: () => nextSlide(),
    });

    useSwipe({
        ref: carousel,
        onSwipeLeft: () => nextSlide(),
        onSwipeRight: () => prevSlide(),
    });
    const heroVideo = (
        embedUrl: string,
        croppedLogoUrl480?: string,
        croppedLogoUrl600?: string,
        croppedLogoUrl940?: string,
    ) => (
        <HeroVideo url={embedUrl}>
            <HeroImage
                croppedLogoUrl480={croppedLogoUrl480 || ''}
                croppedLogoUrl600={croppedLogoUrl600 || ''}
                croppedLogoUrl940={croppedLogoUrl940 || ''}
                alt={''}
                fetchPriority={FetchPriorityType.high}
            />
        </HeroVideo>
    );
    return (
        <CarouselWrapper
            ref={carousel}
            tabIndex={shouldShowCarouselControls ? 0 : -1}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            data-testid="hero-carousel"
        >
            {shouldShowCarouselControls && (
                <>
                    <ul aria-hidden={true}>
                        <li>
                            <ControlButton
                                onClick={prevSlide}
                                action="prev"
                                data-heap-id={
                                    LISTING_HEAP_IDS.LEFT_ARROW_HERO_CAROUSEL
                                }
                            >
                                <Icon type={<ArrowLeftChunky />} />
                                <AccessibleText>
                                    {gettext('Previous slide')}
                                </AccessibleText>
                            </ControlButton>
                        </li>
                        <li>
                            <ControlButton
                                onClick={nextSlide}
                                action="next"
                                data-heap-id={
                                    LISTING_HEAP_IDS.RIGHT_ARROW_HERO_CAROUSEL
                                }
                            >
                                <Icon type={<ArrowRightChunky />} />
                                <AccessibleText>
                                    {gettext('Next slide')}
                                </AccessibleText>
                            </ControlButton>
                        </li>
                    </ul>
                    <SliderBarOverlay />
                    <SliderBar aria-hidden={true}>
                        <SliderBarList>
                            {items.map((_, index: number) => {
                                const slideNumber = index + 1;

                                return (
                                    <li key={index} data-testid="slider-bar">
                                        <SliderBarButton
                                            onClick={() => {
                                                setCurrentSlide(index);
                                                setTranslationUnit(
                                                    getSlidesTranslationValue(
                                                        index,
                                                    ),
                                                );
                                            }}
                                            active={index === currentSlide}
                                            aria-current={
                                                index === currentSlide
                                            }
                                        >
                                            <AccessibleText>
                                                {gettext(
                                                    'Go to slide %(slideNumber)s',
                                                    { slideNumber },
                                                )}
                                            </AccessibleText>
                                        </SliderBarButton>
                                    </li>
                                );
                            })}
                        </SliderBarList>
                    </SliderBar>
                </>
            )}
            {items.length > 0 ? (
                <CarouselViewport>
                    <ImageList
                        style={{ transform: `translateX(${translationUnit}%)` }}
                        data-testid="slide-list"
                    >
                        {items.map((item, index: number) => {
                            const shouldShowHeroVideo =
                                index === 0 && featuredVideo?.embedUrl;
                            return (
                                <ImageListItem
                                    data-testid="slide"
                                    aria-hidden={!(index === firstSlide)}
                                    key={`slide-${index}`}
                                >
                                    {shouldShowHeroVideo ? (
                                        heroVideo(
                                            featuredVideo.embedUrl,
                                            item.croppedLogoUrl480,
                                            item.croppedLogoUrl600,
                                            item.croppedLogoUrl940,
                                        )
                                    ) : (
                                        <HeroImage
                                            croppedLogoUrl480={
                                                item.croppedLogoUrl480
                                            }
                                            croppedLogoUrl600={
                                                item.croppedLogoUrl600
                                            }
                                            croppedLogoUrl940={
                                                item.croppedLogoUrl940
                                            }
                                            alt={index === 0 ? eventName : ''}
                                            loading={
                                                index !== 0
                                                    ? LoadingType.lazy
                                                    : undefined
                                            }
                                            fetchPriority={
                                                index === 0
                                                    ? FetchPriorityType.high
                                                    : FetchPriorityType.low
                                            }
                                        />
                                    )}
                                </ImageListItem>
                            );
                        })}
                    </ImageList>
                </CarouselViewport>
            ) : (
                heroVideo(
                    featuredVideo?.embedUrl || '',
                    featuredVideo?.thumbnail?.small?.url || '',
                    featuredVideo?.thumbnail?.medium?.url || '',
                    featuredVideo?.thumbnail?.large?.url || '',
                )
            )}
        </CarouselWrapper>
    );
};
